import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { initNotificationSound, playNotificationSound } from '../utils/sound';

type WebSocketMessage = {
  type: 'notification' | 'leave_update' | 'expense_update' | 'training_update';
  data: any;
};

type MessageHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private handlers: Set<MessageHandler> = new Set();
  private url: string;
  private token: string | null = null;

  constructor() {
    const envUrl = (import.meta as any)?.env?.VITE_WS_BASE_URL as string | undefined;

    if (envUrl && typeof window !== 'undefined') {
      if (/^wss?:\/\//i.test(envUrl)) {
        this.url = envUrl;
      } else {
        const path = envUrl.startsWith('/') ? envUrl : `/${envUrl}`;
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        this.url = `${protocol}//${window.location.host}${path}`;
      }
    } else if (typeof window !== 'undefined') {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      this.url = `${wsProtocol}//${wsHost}/ws`;
    } else {
      this.url = 'ws://localhost/ws';
    }

    if (typeof console !== 'undefined') {
      console.debug('[WebSocketService] using endpoint', this.url);
    }
  }

  connect(token: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.token = token;
    
    try {
      this.ws = new WebSocket(`${this.url}?token=${token}`);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        // Prepare audio unlocking on first user gesture
        initNotificationSound();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          if (message?.type === 'notification') {
            try { playNotificationSound(); } catch { /* noop */ }
          }
          this.handlers.forEach(handler => handler(message));
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.attemptReconnect();
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token);
      }
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.handlers.clear();
  }

  subscribe(handler: MessageHandler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  send(message: WebSocketMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }
}

export const wsService = new WebSocketService();

// React Hook for WebSocket
export const useWebSocket = (onMessage?: MessageHandler) => {
  const { token } = useAuthStore();
  const handlerRef = useRef(onMessage);

  useEffect(() => {
    handlerRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!token) return;

    wsService.connect(token);

    const unsubscribe = handlerRef.current
      ? wsService.subscribe(handlerRef.current)
      : undefined;

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [token]);

  const sendMessage = useCallback((message: WebSocketMessage) => {
    wsService.send(message);
  }, []);

  return { sendMessage };
};

export default wsService;
